import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch, apiJson, ApiError } from '@/lib/apiClient';
import { unreadCountQueryKey } from '@/features/alerts/useAlerts';
import type { ActiveSosResponse, AcknowledgeResponse } from './types';

/**
 * The active-SOS feed (slice 85, PLAN §8). An SOS is the loudest thing this
 * product ever shows, so it has TWO freshness paths that converge on ONE query:
 *
 *   1. THE HUB NUDGE — the server pushes `{ type: 'sos' }` (no payload) over the
 *      live socket the instant the device-state worker writes the alert row.
 *      `useLiveSocket` answers it by invalidating THIS key (seam S3), so the
 *      banner appears within the ~1.5-2 s Raad-hop budget, not on the next poll.
 *   2. THE 15 s POLL BACKSTOP — if the socket is down (a backgrounded tab, a
 *      dropped connection, a read-only impersonation session that never opens
 *      one), the banner must still raise. 15 s is deliberately tighter than the
 *      60 s alerts-badge poll: a panic button is not a geofence crossing.
 *
 * 🔴 THE KEY IS EXPORTED because `useLiveSocket` must invalidate it by value
 * (it cannot import this hook without a cycle — the socket is mounted above every
 * consumer). One constant, imported both places, so the nudge can never target a
 * key the query does not use.
 */
export const activeSosQueryKey = ['alerts', 'active-sos'] as const;

export function useActiveSos(opts: { enabled?: boolean } = {}): UseQueryResult<ActiveSosResponse> {
  return useQuery({
    queryKey: activeSosQueryKey,
    enabled: opts.enabled ?? true,
    queryFn: ({ signal }) => apiJson<ActiveSosResponse>('/api/alerts/active-sos', { signal }),
    // The poll BACKSTOP (path 2 above). Runs in the foreground only — a
    // backgrounded tab on a metered Mogadishu connection costs nothing, and the
    // socket nudge (path 1) covers the moment it returns to the foreground.
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    // Returning to the tab is exactly when a missed SOS matters most.
    refetchOnWindowFocus: true,
    // A transient failure must keep the previous banner rather than blanking it:
    // never treat "the refetch failed" as "the emergency is over".
    retry: 2,
  });
}

/**
 * Acknowledge one SOS (PLAN §8, §9b) — `POST /api/alerts/:id/acknowledge`, no
 * body. On success every SOS surface re-reads: the active-SOS banner clears (the
 * acked row drops out of the active list), the alerts feed re-reads so the history
 * row flips to "Acknowledged by …", and the unread badge re-reads.
 *
 * 🔴 ACK IS A WRITE THAT IS ALLOWED WHILE SUSPENDED (D4). Unlike the ordinary
 * `tenant_suspended` 402 path, acknowledging a panic alarm is permitted for a
 * read-only (unpaid) tenant — the server enforces that; the client just posts.
 */
export function useAcknowledgeSos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (alertId: string): Promise<AcknowledgeResponse> => {
      const res = await apiFetch(`/api/alerts/${encodeURIComponent(alertId)}/acknowledge`, {
        method: 'POST',
      });
      if (!res.ok) throw new ApiError(res.status, null);
      return (await res.json()) as AcknowledgeResponse;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: activeSosQueryKey });
      void qc.invalidateQueries({ queryKey: ['alerts'] });
      void qc.invalidateQueries({ queryKey: unreadCountQueryKey });
    },
  });
}
